/**
 * 万镜一刻 (Yike) Aliyun API client — browser-native, zero dependencies.
 * Uses Aliyun Signature Version 3 (ACS3-HMAC-SHA256) via Web Crypto API.
 *
 * API doc: https://api.aliyun.com/document/Yike/2026-03-19/SubmitYikeAvatarNarratorJob
 */

export const DEFAULT_YIKE_ENDPOINT = 'yike.cn-shanghai.aliyuncs.com';
export const YIKE_API_VERSION = '2026-03-19';

export interface YikeCredentials {
  accessKeyId: string;
  accessKeySecret: string;
  endpoint?: string; // default: DEFAULT_YIKE_ENDPOINT
}

export interface YikeSubmitOptions {
  /** Story text to narrate. Will be trimmed to 3000 chars. */
  script: string;
  /** Video title shown in the Yike console. */
  title?: string;
  /** Pre-built avatar ID from the Yike console. Defaults to the first available. */
  avatarId?: string;
  /** TTS voice ID. Defaults to the platform default. */
  voiceId?: string;
}

export interface YikeJobResult {
  jobId: string;
  requestId: string;
}

export type YikeJobState = 'Pending' | 'Running' | 'Succeeded' | 'Failed';

export interface YikeJobStatus {
  jobId: string;
  state: YikeJobState;
  /** Present when state === 'Succeeded'. CDN URL of the generated MP4. */
  videoUrl?: string;
  /** Present when state === 'Failed'. */
  failReason?: string;
}

// ─── Web Crypto helpers ────────────────────────────────────────────────────────

const enc = new TextEncoder();

async function sha256Hex(data: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(data));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function hmacSha256Hex(secret: string, message: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw', enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false, ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
  return [...new Uint8Array(sig)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function isoDate(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// ─── Aliyun v3 signature ───────────────────────────────────────────────────────

async function buildAuthHeaders(
  action: string,
  endpoint: string,
  bodyJson: string,
  creds: YikeCredentials,
): Promise<Record<string, string>> {
  const nonce = uuid();
  const date = isoDate();
  const bodyHash = await sha256Hex(bodyJson);

  // Signed headers — must be sorted alphabetically
  const signedHeaderNames = [
    'content-type',
    'host',
    'x-acs-action',
    'x-acs-content-sha256',
    'x-acs-date',
    'x-acs-nonce',
    'x-acs-version',
  ];

  const canonicalHeaders = [
    `content-type:application/json; charset=utf-8`,
    `host:${endpoint}`,
    `x-acs-action:${action}`,
    `x-acs-content-sha256:${bodyHash}`,
    `x-acs-date:${date}`,
    `x-acs-nonce:${nonce}`,
    `x-acs-version:${YIKE_API_VERSION}`,
  ].join('\n') + '\n';

  const signedHeadersStr = signedHeaderNames.join(';');

  const canonicalRequest = [
    'POST',
    '/',
    '',
    canonicalHeaders,
    signedHeadersStr,
    bodyHash,
  ].join('\n');

  const hashedCanonical = await sha256Hex(canonicalRequest);
  const stringToSign = `ACS3-HMAC-SHA256\n${hashedCanonical}`;
  const signature = await hmacSha256Hex(creds.accessKeySecret, stringToSign);

  const authorization =
    `ACS3-HMAC-SHA256 Credential=${creds.accessKeyId},` +
    `SignedHeaders=${signedHeadersStr},` +
    `Signature=${signature}`;

  return {
    'Authorization': authorization,
    'Content-Type': 'application/json; charset=utf-8',
    'Host': endpoint,
    'x-acs-action': action,
    'x-acs-content-sha256': bodyHash,
    'x-acs-date': date,
    'x-acs-nonce': nonce,
    'x-acs-version': YIKE_API_VERSION,
  };
}

// ─── API calls ─────────────────────────────────────────────────────────────────

async function callYike<T>(
  action: string,
  body: Record<string, unknown>,
  creds: YikeCredentials,
): Promise<T> {
  const endpoint = (creds.endpoint || DEFAULT_YIKE_ENDPOINT).replace(/^https?:\/\//, '');
  const url = `https://${endpoint}/`;
  const bodyJson = JSON.stringify(body);
  const headers = await buildAuthHeaders(action, endpoint, bodyJson, creds);

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: bodyJson,
      mode: 'cors',
    });
  } catch (e) {
    // Network / CORS error
    if (e instanceof TypeError && String(e).includes('Failed to fetch')) {
      throw new YikeCorsError(
        `Cannot reach ${endpoint} from the browser — ` +
        `the Yike API endpoint may block cross-origin requests. ` +
        `Try setting "Proxy via BO" or contact your admin.`,
      );
    }
    throw e;
  }

  const text = await res.text();
  let data: Record<string, unknown>;
  try { data = JSON.parse(text) as Record<string, unknown>; } catch { data = {}; }

  if (!res.ok) {
    const code = (data['Code'] as string) || res.status.toString();
    const msg = (data['Message'] as string) || text.slice(0, 300);
    throw new Error(`Yike API ${code}: ${msg}`);
  }
  return data as unknown as T;
}

export class YikeCorsError extends Error {
  readonly isCors = true;
}

// ─── Public API ────────────────────────────────────────────────────────────────

/** Submit a narrator video generation job. Returns the job ID to poll. */
export async function submitNarratorJob(
  opts: YikeSubmitOptions,
  creds: YikeCredentials,
): Promise<YikeJobResult> {
  const script = opts.script.slice(0, 3000);
  const body: Record<string, unknown> = {
    Script: script,
    Title: opts.title || 'Fable Story',
  };
  if (opts.avatarId) body['AvatarId'] = opts.avatarId;
  if (opts.voiceId) body['VoiceId'] = opts.voiceId;

  const resp = await callYike<Record<string, unknown>>(
    'SubmitYikeAvatarNarratorJob', body, creds,
  );

  const dataBlock = resp['Data'] as Record<string, unknown> | undefined;
  const jobId = (resp['JobId'] ?? (dataBlock && dataBlock['JobId'])) as string | undefined;
  const requestId = (resp['RequestId'] as string) || '';
  if (!jobId) throw new Error('Yike: no JobId in response — check your API parameters');
  return { jobId, requestId };
}

/** Poll once for job status. */
export async function getJobStatus(
  jobId: string,
  creds: YikeCredentials,
): Promise<YikeJobStatus> {
  const resp = await callYike<Record<string, unknown>>(
    'GetYikeAvatarNarratorJob', { JobId: jobId }, creds,
  );

  const data = (resp['Data'] as Record<string,unknown>) ?? resp;
  const state = (data['State'] as YikeJobState | undefined) || 'Pending';
  const videoUrl = (data['VideoUrl'] as string | undefined) || (data['OutputVideoUrl'] as string | undefined);
  const failReason = (data['FailReason'] as string | undefined) || (data['Message'] as string | undefined);

  return { jobId, state, videoUrl, failReason };
}

/**
 * Submit a job and poll until completion (or timeout / error).
 * Calls `onProgress` every poll tick with current status.
 */
export async function generateVideo(
  opts: YikeSubmitOptions,
  creds: YikeCredentials,
  onProgress?: (status: YikeJobStatus) => void,
  maxWaitMs = 300_000,
  pollIntervalMs = 6_000,
): Promise<YikeJobStatus> {
  const { jobId } = await submitNarratorJob(opts, creds);
  const deadline = Date.now() + maxWaitMs;

  while (Date.now() < deadline) {
    await new Promise<void>((r) => setTimeout(r, pollIntervalMs));
    const status = await getJobStatus(jobId, creds);
    onProgress?.(status);
    if (status.state === 'Succeeded' || status.state === 'Failed') return status;
  }
  throw new Error('Yike: video generation timed out after 5 minutes');
}
